import { useLazyQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { ME_QUERY } from "@/graphql/auth";
import { getAllDemoUsers, getDemoUserByEmail, getDemoUsersSections } from "@/lib/config/demo-users";
import { authService } from "@/lib/services/auth.service";
import { useAuthStore } from "@/lib/store/auth-enhanced";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginForm = z.infer<typeof loginSchema>;

export function useLoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const { t } = useTranslation("auth");
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [getMeQuery] = useLazyQuery(ME_QUERY);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // Pre-fill email from query params or localStorage (do this in effect to avoid render-time mutations)
  // Use specific form methods in deps and compute URLSearchParams inside the effect so
  // values are stable and lint rules are satisfied.
  useEffect(() => {
    try {
      const { getValues, setValue } = form;

      const searchParams = new URLSearchParams(location.search);
      const emailFromQuery = searchParams.get("email");
      const redirectFromQuery = searchParams.get("redirect");

      if (emailFromQuery) {
        if (!getValues("email")) setValue("email", emailFromQuery);
        // Store redirect URL so we can navigate after successful login
        if (redirectFromQuery) {
          localStorage.setItem("salon:loginRedirect", redirectFromQuery);
        }
        return;
      }

      const stored = localStorage.getItem("salon:lastEmail");
      if (stored && !getValues("email")) {
        setValue("email", stored);
      }
    } catch (_e) {
      // Ignore errors (SSR or blocked storage)
    }
  }, [location.search, form.getValues, form.setValue, form]);

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);

    try {
      // Step 1: Login via auth service
      setLoadingMessage("Signing in...");
      const { tokens, user } = await authService.login({
        email: data.email,
        password: data.password,
      });

      // Update auth store
      login(tokens, user);

      // Always persist last-used email for convenience
      try {
        localStorage.setItem("salon:lastEmail", data.email);
      } catch (_e) {
        // Ignore (SSR or blocked storage)
      }

      // Step 2: Get full user profile
      setLoadingMessage("Loading profile...");
      const meResult = await getMeQuery({
        context: {
          headers: {
            authorization: `Bearer ${tokens.accessToken}`,
          },
        },
      });

      if (!meResult.data?.me) {
        toast.error("Profile loading failed", {
          description: "Unable to load your profile. Please try logging in again.",
        });
        return;
      }

      // Show success message - franchise/salon loading will be handled by AppContainer
      toast.success("Welcome back!", {
        description: "Loading your workspace...",
      });

      // Check for stored redirect URL (e.g. from invitation page)
      const loginRedirect = localStorage.getItem("salon:loginRedirect");
      if (loginRedirect) {
        localStorage.removeItem("salon:loginRedirect");
        navigate(loginRedirect);
      } else {
        // Navigate to home - AppContainer will handle franchise loading and salon selection
        navigate("/");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      let errorTitle = "Login failed";
      let errorMessage = "Please check your credentials and try again.";

      // Handle different error codes from AuthService
      if (error.code) {
        switch (error.code) {
          case "invalidCredentials":
            errorTitle = "Invalid credentials";
            errorMessage = "The email or password you entered is incorrect. Please try again.";
            break;
          case "userNotFound":
            errorTitle = "Account not found";
            errorMessage = "No account found with this email address.";
            break;
          case "userDisabled":
            errorTitle = "Account disabled";
            errorMessage = "Your account has been disabled. Please contact support for assistance.";
            break;
          case "tokenExpired":
          case "sessionExpired":
            errorTitle = "Session expired";
            errorMessage = "Your session has expired. Please log in again.";
            break;
          case "unauthorized":
            errorTitle = "Access denied";
            errorMessage = "You are not authorized to access this system.";
            break;
          case "networkError":
            errorTitle = "Connection error";
            errorMessage =
              "Unable to connect to the server. Please check your internet connection and try again.";
            break;
          default:
            errorTitle = "Login failed";
            errorMessage = error.message || "An unexpected error occurred. Please try again.";
        }
      } else {
        // Handle other types of errors (GraphQL, network, etc.)
        if (error.networkError) {
          errorTitle = "Connection error";
          errorMessage = "Unable to connect to the server. Please check your internet connection.";
        } else if (error.graphQLErrors?.length > 0) {
          const gqlError = error.graphQLErrors[0];
          errorTitle = "Server error";
          errorMessage = gqlError.message || "A server error occurred. Please try again.";
        }
      }

      toast.error(errorTitle, {
        description: errorMessage,
        duration: 5000, // Show longer for better UX
      });
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Get demo users from configuration
  const demoSections = getDemoUsersSections(t);
  const allDemoUsers = getAllDemoUsers(t);

  const loginWith = (credentials: LoginForm) => {
    form.setValue("email", credentials.email);
    form.setValue("password", credentials.password);
    form.handleSubmit(onSubmit)();
  };

  const loginWithDemo = (userEmail?: string) => {
    const user = userEmail ? getDemoUserByEmail(userEmail, t) : allDemoUsers[0];

    if (user) {
      loginWith(user);
    }
  };

  return {
    form,
    isLoading,
    loadingMessage,
    onSubmit: form.handleSubmit(onSubmit),
    loginWith,
    loginWithDemo,
    demoSections,
    allDemoUsers,
  };
}
